import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, Check, RotateCcw, ExternalLink } from "lucide-react";
import { teacherApi } from "@/lib/api";
import { Section } from "./_shared";

/**
 * „Twoje pierwsze 24h na produkcji" — interaktywna checklista dla admina
 * uruchamiającego betę. Większość kroków wykrywa się automatycznie (env/DB),
 * niektóre wymagają ręcznego odhaczenia (np. „otworzyłem /status w prywatnym").
 */
export default function OnboardingChecklistTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await teacherApi.get("/admin/onboarding/checklist");
      setData(data);
    } catch { toast.error("Nie udało się pobrać checklisty"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    try {
      if (item.manual_done_at) {
        await teacherApi.post(`/admin/onboarding/uncheck/${item.id}`);
      } else {
        await teacherApi.post(`/admin/onboarding/check/${item.id}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd zapisu"); }
  };

  if (loading) return <Section icon={Rocket} title="Twoje pierwsze 24h"><div>Ładowanie…</div></Section>;
  if (!data) return null;

  const pctColor =
    data.pct === 100 ? "bg-[#86EFAC]"
    : data.pct >= 60 ? "bg-[#FEF08A]"
    : "bg-[#FCA5A5]";

  return (
    <Section icon={Rocket} title="Twoje pierwsze 24h na produkcji">
      {/* Progress banner */}
      <div
        className={`brut border-4 border-[#0F172A] rounded-md p-4 mb-6 shadow-[6px_6px_0_#0F172A] ${pctColor}`}
        data-testid="onboarding-progress-banner"
      >
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <div className="font-black text-2xl uppercase">
              {data.done} / {data.total} kroków ({data.pct}%)
            </div>
            <div className="text-sm mt-1 font-bold">
              {data.pct === 100
                ? "🎉 Wszystko gotowe. Powodzenia na becie!"
                : data.pct >= 60
                ? "Prawie gotowe — zostało kilka kroków."
                : "Zacznij od kroków z podpowiedziami poniżej."}
            </div>
          </div>
          <button
            onClick={load}
            className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md px-3 py-1.5 font-bold text-sm"
            data-testid="onboarding-refresh-btn"
          >
            Odśwież
          </button>
        </div>
        {/* Progress bar */}
        <div className="mt-3 brut border-2 border-[#0F172A] rounded bg-white h-4 overflow-hidden">
          <div
            className="h-full bg-[#0F172A] transition-all"
            style={{ width: `${data.pct}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-2" data-testid="onboarding-steps">
        {data.items.map((item, idx) => (
          <StepRow key={item.id} idx={idx + 1} item={item} onToggle={() => toggle(item)} />
        ))}
      </div>

      <div className="mt-6 text-xs text-[#64748B]">
        💡 Kroki oznaczone „auto" wykrywają się same (env vars / stan DB). Pozostałe
        wymagają ręcznego kliknięcia — mimo automatycznego wsparcia, admin decyduje
        kiedy uzna je za zrobione.
      </div>
    </Section>
  );
}


function StepRow({ idx, item, onToggle }) {
  const done = item.done;
  const bg = done ? "bg-[#D1FAE5]" : "bg-white";
  return (
    <div
      className={`brut border-2 border-[#0F172A] rounded-md p-3 ${bg} flex items-start gap-3`}
      data-testid={`onboarding-step-${item.id}`}
    >
      <button
        onClick={onToggle}
        disabled={item.auto_done && !item.manual_done_at}
        className={`brut border-2 border-[#0F172A] rounded w-8 h-8 flex-shrink-0 flex items-center justify-center font-black ${
          done ? "bg-[#10B981] text-white" : "bg-white hover:bg-[#F1F5F9]"
        } ${item.auto_done && !item.manual_done_at ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
        title={item.auto_done && !item.manual_done_at
          ? "Wykryte automatycznie — nie można odhaczyć ręcznie"
          : done ? "Kliknij aby cofnąć" : "Kliknij aby oznaczyć jako zrobione"}
        data-testid={`onboarding-toggle-${item.id}`}
      >
        {done ? <Check className="w-4 h-4" strokeWidth={3} /> : idx}
      </button>
      <div className="flex-1">
        <div className="font-black text-sm">
          <span className="mr-1">{item.emoji}</span>
          {item.title}
          {item.auto_done && (
            <span className="ml-2 text-[10px] font-black uppercase text-[#10B981]">✓ auto</span>
          )}
          {item.manual_done_at && !item.auto_done && (
            <span className="ml-2 text-[10px] font-black uppercase text-[#64748B]">manual</span>
          )}
        </div>
        <div className="text-xs text-[#334155] mt-0.5">{item.hint}</div>
      </div>
      {done && item.manual_done_at && !item.auto_done && (
        <button
          onClick={onToggle}
          className="brut brut-hover bg-white border-2 border-[#0F172A] rounded px-2 py-0.5 text-xs font-bold flex-shrink-0"
          data-testid={`onboarding-uncheck-${item.id}`}
          title="Cofnij oznaczenie"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
